import React, { useState } from "react";
import {
  Box, Typography, Stack, Divider, Button, Paper, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { subscriptionApi } from "../../api/subscriptionApi";
import CustomSnackbar from "./CustomSnackbar";

const CGST_RATE = 0.09;
const SGST_RATE = 0.09;

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(n);

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function CheckoutModal({ open, onClose, plan, onSuccess }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showMessage = (msg, sev = "info") => {
    setSnackbar({ open: true, message: msg, severity: sev });
  };

  if (!plan) {
    return null;
  }

  const subtotal = Number(plan.price);
  const cgst = +(subtotal * CGST_RATE).toFixed(2);
  const sgst = +(subtotal * SGST_RATE).toFixed(2);
  const total = +(subtotal + cgst + sgst).toFixed(2);

  const handlePay = async () => {
    setLoading(true);
    try {
      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        showMessage("Razorpay SDK failed to load. Are you offline?", "error");
        setLoading(false);
        return;
      }

      // Step 1: create Razorpay order
      const orderRes = await subscriptionApi.createOrder({
        plan_id: plan.id,
        grand_total: total,
      });

      // Handle non-200 responses from createOrder
      if (!orderRes || orderRes.status !== 200 || !orderRes.data) {
        const errorMsg = orderRes?.message || "Failed to initiate payment. Please try again.";
        showMessage(errorMsg, "error");
        setLoading(false);
        return;
      }

      const data = orderRes.data;

      // Step 2: open Razorpay checkout
      const options = {
        key: data.key_id,
        amount: Number(data.amount),
        currency: "INR",
        order_id: data.razorpay_order_id,

        // product details
        name: "MeruBot",
        description: `Payment of plan ${data.notes.plan_name} for ${data.notes.credit} credits`,
        image: "https://merubot.com/static/media/merubot_icon.9551d73a384b683de124.png",

        prefill: {
          name: data.notes.user_name,
          email: data.notes.user_email,
        },

        // custome payload send to backend
        notes: data.notes,

        // called when modal is closed
        handler: async function (paymentResponse) {
          try {
            // Step 3: verify payment on backend
            const verifyRes = await subscriptionApi.verifyPayment({
              notes: data.notes,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });

            if (!verifyRes) {
              showMessage("Payment verification failed. Please contact support.", "error");
              setLoading(false);
              return;
            }

            if (verifyRes.status === 200) {
              showMessage(verifyRes.message || "Subscription upgraded successfully!", "success");
              if (onSuccess) onSuccess();
              onClose();
            } else if (verifyRes.status === 202) {
              showMessage(
                verifyRes.message || "Payment verified via Razorpay but pending database update. Please check again in a few moments.",
                "warning"
              );
              if (onSuccess) onSuccess();
              onClose();
            } else {
              showMessage(verifyRes.message || "Payment verification failed. Please contact support.", "error");
              setLoading(false);
            }
          } catch (verifyErr) {
            console.error("Payment verification failed", verifyErr);
            showMessage("An error occurred during payment verification.", "error");
          } finally {
            setLoading(false);
          }
        },

        modal: {
          ondismiss: () => setLoading(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error(err);
      showMessage("Failed to start payment process.", "error");
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={() => !loading && onClose()}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        },
      }}
    >
      <DialogTitle
        variant="h5"
        align="center"
        sx={{
          fontWeight: 700,
          color: theme.palette.primary.main,
          pb: 0.3,
        }}>
        Checkout
      </DialogTitle>


      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          textAlign: "center",
          mb: 1,
          fontSize: "0.85rem",
        }}>
        Varify your payment and plan details.
      </Typography>
      <Divider />

      <DialogContent dividers>
        <Box >
          {/* Secure badge */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Secure Checkout&nbsp;
              <Typography variant="caption" color="text.disabled" component="span">
                256-bit SSL encrypted
              </Typography>
            </Typography>
          </Box>

          {/* 1. Plan & Billing */}
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
            1. Plan &amp; Billing
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography fontWeight={600}>{plan.plan_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {plan.credit} credits · {plan.allowed_recruiter_count} recruiters
                </Typography>
              </Box>
              <Typography fontWeight={600}>{fmt(subtotal)}</Typography>
            </Box>
          </Paper>

          {/* 2. Price Details */}
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
            2. Price Details
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              <Row label="Subtotal" value={fmt(subtotal)} />
              <Row label="CGST (9%)" value={fmt(cgst)} />
              <Row label="SGST (9%)" value={fmt(sgst)} />
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography fontWeight={600}>Total</Typography>
                  <Chip label="18% GST" size="small" color="primary" variant="outlined"
                    sx={{ fontSize: 11, height: 20, borderRadius: 1 }} />
                </Box>
                <Typography fontWeight={700} color="primary.main" fontSize={16}>
                  {fmt(total)}
                </Typography>
              </Box>
            </Stack>

            {/* Savings banner — optional, remove if no savings apply */}
            {plan.savings && (
              <Box
                sx={{
                  mt: 2, px: 1.5, py: 1, bgcolor: "success.50",
                  border: "1px solid", borderColor: "success.200",
                  borderRadius: 1.5, display: "flex", alignItems: "center", gap: 1,
                }}
              >
                <CheckCircleOutlinedIcon sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="caption" color="success.dark">
                  You save {fmt(plan.savings)} compared to monthly
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} width="100%">
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handlePay}
            disabled={loading}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Pay ${fmt(total)}`
            )}
          </Button>
        </Stack>
      </DialogActions>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Box>
  );
}
